// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: New task on the desktop (concept 5): title, description, chips for
// status (the chosen list's statuses), assignees (its members), due date,
// priority, tags, estimate, subtasks and files, and a location picker that
// searches every list in the workspace (recent lists first). Starts in the
// current list. "Create another" keeps it open; ⌘/Ctrl+Enter creates.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/konvo_tasks/components/workspace/desktop/NewTaskModal.module.css';
import {
	AssigneeMenuItems,
	DueLabel,
	DueMenu,
	EstimateMenu,
	membersFor,
	PeoplePile,
	PriorityMenuItems,
	StatusMenuItems,
	TagMenu,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceCells';
import {isClosedType} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceGrouping';
import {
	MenuPopout,
	menuTokens,
	useFocusOnMount,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceMenu';
import {
	PRIORITY_DESCRIPTORS,
	PriorityFlag,
	SpaceTile,
	StatusRing,
} from '@app/features/konvo_tasks/components/workspace/shared/WorkspaceBits';
import KonvoTasks, {
	formatDuration,
	type KonvoTask,
	type KonvoTaskListStatus,
	type KonvoTaskUser,
	statusLabel,
} from '@app/features/konvo_tasks/state/KonvoTasks';
import KonvoWorkspace from '@app/features/konvo_tasks/state/KonvoWorkspace';
import {searchLists, splitMatch} from '@app/features/konvo_tasks/utils/ListSearch';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	CalendarBlankIcon,
	CaretDownIcon,
	FlagIcon,
	ListChecksIcon,
	ListIcon,
	MagnifyingGlassIcon,
	PaperclipIcon,
	PlusIcon,
	SparkleIcon,
	TagIcon,
	TimerIcon,
	UserPlusIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {forwardRef, useEffect, useLayoutEffect, useRef, useState} from 'react';

const DESCRIPTORS = {
	title: msg({message: 'New task', comment: 'New task modal title.'}),
	name: msg({message: 'Task name', comment: 'New task modal: title placeholder.'}),
	description: msg({
		message: 'Add a description',
		comment: 'New task modal: description placeholder.',
	}),
	status: msg({message: 'Status', comment: 'New task modal chip.'}),
	assignees: msg({message: 'Assignees', comment: 'New task modal chip.'}),
	due: msg({message: 'Due date', comment: 'New task modal chip.'}),
	priority: msg({message: 'Priority', comment: 'New task modal chip.'}),
	tags: msg({message: 'Tags', comment: 'New task modal chip.'}),
	estimate: msg({message: 'Estimate', comment: 'New task modal chip.'}),
	subtasks: msg({message: 'Subtasks', comment: 'New task modal chip.'}),
	subtaskPlaceholder: msg({message: 'Subtask name, Enter for another', comment: 'New task modal subtask input.'}),
	removeSubtask: msg({message: 'Remove subtask', comment: 'New task modal.'}),
	attach: msg({message: 'Attach', comment: 'New task modal chip.'}),
	removeFile: msg({message: 'Remove file', comment: 'New task modal.'}),
	pickList: msg({message: 'Choose a list', comment: 'New task modal: location chip without a list.'}),
	searchLists: msg({message: 'Search lists', comment: 'New task modal location search placeholder.'}),
	recent: msg({message: 'Recent', comment: 'New task modal location picker section.'}),
	suggested: msg({
		message: 'Suggested',
		comment: 'Task list picker section: lists Konvo suggests for a drafted task, each with its reason.',
	}),
	allLists: msg({message: 'All lists', comment: 'New task modal location picker section.'}),
	noLists: msg({message: 'No lists match.', comment: 'New task modal location picker empty state.'}),
	another: msg({message: 'Create another', comment: 'New task modal toggle.'}),
	cancel: msg({message: 'Cancel', comment: 'New task modal button.'}),
	create: msg({message: 'Create task', comment: 'New task modal button.'}),
	close: msg({message: 'Close', comment: 'New task modal close button.'}),
	created: msg({message: 'Task created in {list}.', comment: 'New task modal success toast.'}),
	failed: msg({message: 'Could not create the task. Try again.', comment: 'New task modal error toast.'}),
	needList: msg({message: 'Choose a list for the task.', comment: 'New task modal validation toast.'}),
};

export interface NewTaskOptions {
	listId?: string | null;
	name?: string;
	onCreated?: (task: {id: string; url: string}) => void;
}

export function openNewTaskModal(options: NewTaskOptions = {}): void {
	ModalCommands.push(modal(() => <NewTaskModal {...options} onClose={() => ModalCommands.pop()} />));
}

function firstOpen(statuses: ReadonlyArray<KonvoTaskListStatus>): KonvoTaskListStatus | null {
	return statuses.find((status) => !isClosedType(status.type)) ?? statuses[0] ?? null;
}

export interface SuggestedList {
	listId: string;
	/** Why it is suggested, shown under the name. */
	note: string;
}

/** Search every list in the workspace; drafted tasks add their suggestions (with reasons) on top. */
export const ListPicker = observer(
	({
		current,
		onPick,
		close,
		suggested = [],
	}: {
		current: string | null;
		onPick: (listId: string) => void;
		close: () => void;
		suggested?: ReadonlyArray<SuggestedList>;
	}) => {
		const {i18n} = useLingui();
		const [query, setQuery] = useState('');
		const [active, setActive] = useState(0);
		const searchRef = useFocusOnMount<HTMLInputElement>();
		const {recent, matches} = searchLists(KonvoWorkspace.allLists, KonvoWorkspace.recentLists, query);
		// Suggestions lead while nothing is typed; the same list is not listed twice.
		const picks = query.trim()
			? []
			: suggested.flatMap((entry) => {
					const node = KonvoWorkspace.findNode('list', entry.listId);
					return node ? [{list: {id: entry.listId, name: node.name}, space: node.space, note: entry.note}] : [];
				});
		const taken = new Set(picks.map((pick) => pick.list.id));
		const recentRows = recent.filter((entry) => !taken.has(entry.list.id));
		const matchRows = matches.filter((entry) => !taken.has(entry.list.id));
		const ids = [...picks.map((pick) => pick.list.id), ...[...recentRows, ...matchRows].map((entry) => entry.list.id)];
		useEffect(() => setActive(0), [query]);
		const pick = (listId: string) => {
			onPick(listId);
			close();
		};
		const row = (entry: (typeof recentRows)[number], index: number) => {
			const [before, hit, after] = splitMatch(entry.list.name, query);
			return (
				<button
					key={entry.list.id}
					type="button"
					role="option"
					aria-selected={index === active}
					className={clsx(styles.pickRow, index === active && styles.pickRowOn)}
					onMouseEnter={() => setActive(index)}
					onClick={() => pick(entry.list.id)}
					data-flx="konvo-tasks.workspace.new-task.list-option"
				>
					<ListIcon size={16} />
					<span className={clsx(styles.pickName, entry.list.id === current && styles.pickCurrent)}>
						{before}
						{hit && <mark>{hit}</mark>}
						{after}
					</span>
					<span className={styles.pickPath}>
						<SpaceTile name={entry.space.name} color={entry.space.color} size={18} />
						{entry.path}
					</span>
				</button>
			);
		};
		return (
			<div
				className={clsx(menuTokens, styles.picker)}
				role="listbox"
				data-flx="konvo-tasks.workspace.new-task.list-picker"
			>
				<label className={styles.pickSearch}>
					<MagnifyingGlassIcon size={16} />
					<input
						ref={searchRef}
						value={query}
						placeholder={i18n._(DESCRIPTORS.searchLists)}
						aria-label={i18n._(DESCRIPTORS.searchLists)}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'ArrowDown') {
								event.preventDefault();
								setActive((value) => Math.min(ids.length - 1, value + 1));
							} else if (event.key === 'ArrowUp') {
								event.preventDefault();
								setActive((value) => Math.max(0, value - 1));
							} else if (event.key === 'Enter') {
								event.preventDefault();
								const listId = ids[active];
								if (listId) pick(listId);
							}
						}}
						data-flx="konvo-tasks.workspace.new-task.list-search"
					/>
				</label>
				<div className={styles.pickList}>
					{picks.length > 0 && (
						<div className={clsx(styles.pickHead, styles.pickHeadSuggested)}>
							<SparkleIcon size={13} />
							{i18n._(DESCRIPTORS.suggested)}
						</div>
					)}
					{picks.map((entry, index) => (
						<button
							key={entry.list.id}
							type="button"
							role="option"
							aria-selected={index === active}
							className={clsx(styles.pickRow, styles.pickRowTall, index === active && styles.pickRowOn)}
							onMouseEnter={() => setActive(index)}
							onClick={() => pick(entry.list.id)}
							data-flx="konvo-tasks.workspace.new-task.list-suggestion"
						>
							<ListIcon size={16} />
							<span className={styles.pickTwo}>
								<span className={clsx(styles.pickName, entry.list.id === current && styles.pickCurrent)}>
									{entry.list.name}
								</span>
								<span className={styles.pickNote}>{entry.note}</span>
							</span>
							<span className={styles.pickPath}>
								<SpaceTile name={entry.space.name} color={entry.space.color} size={18} />
								{entry.space.name}
							</span>
						</button>
					))}
					{recentRows.length > 0 && <div className={styles.pickHead}>{i18n._(DESCRIPTORS.recent)}</div>}
					{recentRows.map((entry, index) => row(entry, picks.length + index))}
					{matchRows.length > 0 && <div className={styles.pickHead}>{i18n._(DESCRIPTORS.allLists)}</div>}
					{matchRows.map((entry, index) => row(entry, picks.length + recentRows.length + index))}
					{ids.length === 0 && <div className={styles.pickEmpty}>{i18n._(DESCRIPTORS.noLists)}</div>}
				</div>
			</div>
		);
	},
);

const Chip = forwardRef<
	HTMLButtonElement,
	React.ButtonHTMLAttributes<HTMLButtonElement> & {set?: boolean; icon: React.ReactNode}
>(({set, icon, children, className, ...rest}, ref) => {
	return (
		<button ref={ref} type="button" className={clsx(styles.chip, set && styles.chipSet, className)} {...rest}>
			{icon}
			{children}
		</button>
	);
});
Chip.displayName = 'NewTaskChip';

const NewTaskModal = observer(
	({listId: initialList, name: initialName, onCreated, onClose}: NewTaskOptions & {onClose: () => void}) => {
		const {i18n} = useLingui();
		const titleRef = useRef<HTMLInputElement | null>(null);
		const descRef = useRef<HTMLTextAreaElement | null>(null);
		const fileRef = useRef<HTMLInputElement | null>(null);
		const focusSubtask = useRef(false);
		const [listId, setListId] = useState<string | null>(
			() =>
				initialList ??
				KonvoWorkspace.recentLists.find((id) => KonvoWorkspace.findNode('list', id)) ??
				KonvoWorkspace.allLists[0]?.list.id ??
				null,
		);
		const [name, setName] = useState(initialName ?? '');
		const [description, setDescription] = useState('');
		const [status, setStatus] = useState<string | null>(null);
		const [assignees, setAssignees] = useState<Array<KonvoTaskUser>>([]);
		const [due, setDue] = useState<number | null>(null);
		const [priority, setPriority] = useState<number | null>(null);
		const [tags, setTags] = useState<Array<string>>([]);
		const [estimate, setEstimate] = useState<number | null>(null);
		const [subtasks, setSubtasks] = useState<Array<string> | null>(null);
		const [files, setFiles] = useState<Array<File>>([]);
		const [another, setAnother] = useState(false);
		const [busy, setBusy] = useState(false);

		useEffect(() => {
			void KonvoWorkspace.loadHierarchy();
		}, []);
		// Opened before the workspace tree was ever loaded: start in a list once it arrives.
		const firstList = KonvoWorkspace.recentListNodes[0]?.id ?? KonvoWorkspace.allLists[0]?.list.id ?? null;
		useEffect(() => {
			if (!listId && firstList) setListId(firstList);
		}, [listId, firstList]);
		useEffect(() => {
			if (listId) void KonvoWorkspace.loadListMeta(listId);
		}, [listId]);
		const statuses = KonvoWorkspace.statusesFor(listId);
		const chosenStatus =
			statuses.find((entry) => (entry.status ?? '').toLowerCase() === (status ?? '').toLowerCase()) ??
			firstOpen(statuses);
		useLayoutEffect(() => {
			const element = descRef.current;
			if (!element) return;
			element.style.height = 'auto';
			element.style.height = `${Math.min(element.scrollHeight, 220)}px`;
		}, [description]);

		const node = listId ? KonvoWorkspace.findNode('list', listId) : null;
		const members = membersFor(listId);
		const suggestions = [
			...new Set(
				[...KonvoWorkspace.views.values()].flatMap((view) =>
					view.tasks.flatMap((task) => task.tags.map((tag) => tag.name ?? '')),
				),
			),
		].filter(Boolean);
		const fakeTask = {due_date: due, status: {status: null, color: null, type: 'open'}} as unknown as KonvoTask;

		const reset = () => {
			setName('');
			setDescription('');
			setSubtasks(null);
			setFiles([]);
			titleRef.current?.focus();
		};

		const submit = async () => {
			const trimmed = name.trim();
			if (!trimmed || busy) {
				titleRef.current?.focus();
				return;
			}
			if (!listId) {
				ToastCommands.createToast({type: 'error', children: i18n._(DESCRIPTORS.needList)});
				return;
			}
			setBusy(true);
			try {
				const task = await KonvoWorkspace.createTask({
					listId,
					name: trimmed,
					description: description.trim() || undefined,
					status: chosenStatus?.status ?? undefined,
					assigneeIds: assignees.map((user) => user.id).filter((id): id is number => id != null),
					dueDateMs: due,
					priority,
					tags,
					timeEstimateMs: estimate,
				});
				if (task?.id) {
					for (const subtask of (subtasks ?? []).map((entry) => entry.trim()).filter(Boolean)) {
						await KonvoWorkspace.createTask({listId, name: subtask, parent: task.id}).catch(() => null);
					}
					for (const file of files) await KonvoTasks.uploadAttachment(task.id, file).catch(() => null);
					onCreated?.({id: task.id, url: task.url ?? ''});
				}
				ToastCommands.createToast({type: 'success', children: i18n._(DESCRIPTORS.created, {list: node?.name ?? ''})});
				if (another) reset();
				else onClose();
			} catch {
				ToastCommands.createToast({type: 'error', children: i18n._(DESCRIPTORS.failed)});
			} finally {
				setBusy(false);
			}
		};

		const onKeyDown = (event: React.KeyboardEvent) => {
			if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
				event.preventDefault();
				void submit();
			}
		};

		return (
			<Modal.Root
				size="large"
				onClose={onClose}
				initialFocusRef={titleRef}
				className={styles.root}
				data-flx="konvo-tasks.workspace.new-task"
			>
				<Modal.ScreenReaderLabel text={i18n._(DESCRIPTORS.title)} />
				{/* biome-ignore lint/a11y/noStaticElementInteractions: listens for the ⌘/Ctrl+Enter shortcut from any field inside. */}
				<div className={clsx(menuTokens, styles.body)} onKeyDown={onKeyDown}>
					<div className={styles.head}>
						<b>{i18n._(DESCRIPTORS.title)}</b>
						<button
							type="button"
							className={styles.close}
							aria-label={i18n._(DESCRIPTORS.close)}
							title={i18n._(DESCRIPTORS.close)}
							onClick={onClose}
						>
							<XIcon size={20} />
						</button>
					</div>
					<input
						ref={titleRef}
						className={styles.title}
						value={name}
						placeholder={i18n._(DESCRIPTORS.name)}
						aria-label={i18n._(DESCRIPTORS.name)}
						onChange={(event) => setName(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
								event.preventDefault();
								descRef.current?.focus();
							}
						}}
						data-flx="konvo-tasks.workspace.new-task.name"
					/>
					<textarea
						ref={descRef}
						className={styles.desc}
						rows={1}
						value={description}
						placeholder={i18n._(DESCRIPTORS.description)}
						aria-label={i18n._(DESCRIPTORS.description)}
						onChange={(event) => setDescription(event.target.value)}
						data-flx="konvo-tasks.workspace.new-task.description"
					/>
					<div className={styles.chips}>
						<MenuPopout
							render={(close) => (
								<StatusMenuItems
									statuses={statuses}
									current={chosenStatus?.status ?? null}
									close={close}
									onPick={(entry) => setStatus(entry.status)}
								/>
							)}
						>
							<Chip
								set
								icon={
									chosenStatus ? (
										<StatusRing status={chosenStatus} statuses={statuses} size={15} />
									) : (
										<ListIcon size={16} />
									)
								}
								className={styles.statusChip}
								style={{'--status-color': chosenStatus?.color ?? '#87909f'} as React.CSSProperties}
								data-flx="konvo-tasks.workspace.new-task.status"
							>
								{chosenStatus ? statusLabel(chosenStatus.status) : i18n._(DESCRIPTORS.status)}
							</Chip>
						</MenuPopout>
						<MenuPopout
							wide
							render={() => (
								<AssigneeMenuItems
									members={members}
									selected={assignees}
									onToggle={(user, on) =>
										setAssignees((current) =>
											on ? [...current, user] : current.filter((entry) => entry.id !== user.id),
										)
									}
								/>
							)}
						>
							<Chip
								set={assignees.length > 0}
								icon={assignees.length > 0 ? <PeoplePile users={assignees} size={20} /> : <UserPlusIcon size={16} />}
								data-flx="konvo-tasks.workspace.new-task.assignees"
							>
								{assignees.length > 0
									? assignees
											.map((user) => user.initials ?? (user.username ?? '').split(/\s+/)[0])
											.slice(0, 3)
											.join(', ')
									: i18n._(DESCRIPTORS.assignees)}
							</Chip>
						</MenuPopout>
						<MenuPopout render={(close) => <DueMenu value={due} onPick={setDue} close={close} />}>
							<Chip
								set={due != null}
								icon={<CalendarBlankIcon size={16} />}
								data-flx="konvo-tasks.workspace.new-task.due"
							>
								{due != null ? <DueLabel task={fakeTask} /> : i18n._(DESCRIPTORS.due)}
							</Chip>
						</MenuPopout>
						<MenuPopout render={(close) => <PriorityMenuItems current={priority} onPick={setPriority} close={close} />}>
							<Chip
								set={priority != null}
								icon={priority != null ? <PriorityFlag priority={priority} /> : <FlagIcon size={16} />}
								data-flx="konvo-tasks.workspace.new-task.priority"
							>
								{priority != null ? i18n._(PRIORITY_DESCRIPTORS[priority]!) : i18n._(DESCRIPTORS.priority)}
							</Chip>
						</MenuPopout>
						<MenuPopout
							render={() => (
								<TagMenu
									tags={tags}
									suggestions={suggestions}
									onAdd={(tag) => setTags((current) => (current.includes(tag) ? current : [...current, tag]))}
									onRemove={(tag) => setTags((current) => current.filter((entry) => entry !== tag))}
								/>
							)}
						>
							<Chip set={tags.length > 0} icon={<TagIcon size={16} />} data-flx="konvo-tasks.workspace.new-task.tags">
								{tags.length > 0 ? tags.join(', ') : i18n._(DESCRIPTORS.tags)}
							</Chip>
						</MenuPopout>
						<MenuPopout render={(close) => <EstimateMenu value={estimate} onSet={setEstimate} close={close} />}>
							<Chip
								set={estimate != null}
								icon={<TimerIcon size={16} />}
								data-flx="konvo-tasks.workspace.new-task.estimate"
							>
								{estimate != null ? formatDuration(estimate) : i18n._(DESCRIPTORS.estimate)}
							</Chip>
						</MenuPopout>
						<Chip
							set={Boolean(subtasks?.some((entry) => entry.trim()))}
							icon={<ListChecksIcon size={16} />}
							onClick={() => {
								focusSubtask.current = true;
								setSubtasks((current) => current ?? ['']);
							}}
							data-flx="konvo-tasks.workspace.new-task.subtasks"
						>
							{i18n._(DESCRIPTORS.subtasks)}
						</Chip>
						<Chip
							set={files.length > 0}
							icon={<PaperclipIcon size={16} />}
							onClick={() => fileRef.current?.click()}
							data-flx="konvo-tasks.workspace.new-task.attach"
						>
							{files.length > 0 ? String(files.length) : i18n._(DESCRIPTORS.attach)}
						</Chip>
						<input
							ref={fileRef}
							type="file"
							multiple
							hidden
							onChange={(event) => {
								const picked = Array.from(event.target.files ?? []);
								setFiles((current) => [...current, ...picked]);
								event.target.value = '';
							}}
						/>
					</div>
					{subtasks && (
						<div className={styles.subtasks}>
							{subtasks.map((value, index) => (
								<div key={index} className={styles.subtask}>
									<StatusRing status={{status: 'to do', color: '#87909f', type: 'open'}} size={15} />
									<input
										ref={(element) => {
											if (element && index === subtasks.length - 1 && focusSubtask.current) {
												focusSubtask.current = false;
												element.focus();
											}
										}}
										value={value}
										placeholder={i18n._(DESCRIPTORS.subtaskPlaceholder)}
										onChange={(event) =>
											setSubtasks((current) =>
												(current ?? []).map((entry, at) => (at === index ? event.target.value : entry)),
											)
										}
										onKeyDown={(event) => {
											if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
												event.preventDefault();
												if (value.trim()) {
													focusSubtask.current = true;
													setSubtasks((current) => [...(current ?? []), '']);
												}
											}
										}}
									/>
									<button
										type="button"
										className={styles.remove}
										aria-label={i18n._(DESCRIPTORS.removeSubtask)}
										onClick={() =>
											setSubtasks((current) => {
												const next = (current ?? []).filter((_, at) => at !== index);
												return next.length > 0 ? next : null;
											})
										}
									>
										<XIcon size={14} />
									</button>
								</div>
							))}
						</div>
					)}
					{files.length > 0 && (
						<div className={styles.files}>
							{files.map((file, index) => (
								<span key={`${file.name}-${index}`} className={styles.file}>
									<PaperclipIcon size={14} />
									{file.name}
									<button
										type="button"
										className={styles.remove}
										aria-label={i18n._(DESCRIPTORS.removeFile)}
										onClick={() => setFiles((current) => current.filter((_, at) => at !== index))}
									>
										<XIcon size={13} />
									</button>
								</span>
							))}
						</div>
					)}
					<div className={styles.foot}>
						<Popout
							position="bottom-start"
							offsetMainAxis={8}
							render={({onClose: close}) => <ListPicker current={listId} onPick={setListId} close={close} />}
						>
							<button type="button" className={styles.location} data-flx="konvo-tasks.workspace.new-task.location">
								{node ? (
									<>
										<SpaceTile name={node.space.name} color={node.space.color} size={22} />
										<span className={styles.locationSpace}>{node.space.name}</span>
										<span className={styles.locationSlash}>/</span>
										<span className={styles.locationList}>{node.name}</span>
									</>
								) : (
									<>
										<ListIcon size={16} />
										<span>{i18n._(DESCRIPTORS.pickList)}</span>
									</>
								)}
								<CaretDownIcon size={15} className={styles.locationCaret} />
							</button>
						</Popout>
						<label className={styles.toggle}>
							<input type="checkbox" checked={another} onChange={(event) => setAnother(event.target.checked)} />
							<span className={styles.switch} aria-hidden />
							{i18n._(DESCRIPTORS.another)}
						</label>
						<span className={styles.grow} />
						<button type="button" className={styles.cancel} onClick={onClose}>
							{i18n._(DESCRIPTORS.cancel)}
						</button>
						<button
							type="button"
							className={styles.create}
							disabled={busy || !name.trim() || !listId}
							onClick={() => void submit()}
							data-flx="konvo-tasks.workspace.new-task.create"
						>
							{busy ? <PlusIcon size={16} className={styles.busy} /> : null}
							{i18n._(DESCRIPTORS.create)}
							<kbd className={styles.kbd}>{navigator.platform.toLowerCase().includes('mac') ? '⌘↵' : 'Ctrl ↵'}</kbd>
						</button>
					</div>
				</div>
			</Modal.Root>
		);
	},
);
